import React, { useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Building2,
  FileText,
  UserCheck,
  ClipboardList,
  ClipboardCheck,
  BarChart3,
  TrendingUp,
  DollarSign,
  Ruler,
  Settings,
  KeyRound,
  ChevronDown,
  ChevronRight,
  Menu,
  X,
  UserCircle,
  ShieldCheck,
  Wrench,
  Wallet,
  SlidersHorizontal,
} from 'lucide-react';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import {
  ACCESS_APPLICATIONS,
  ACCESS_INVESTORS,
  ACCESS_MAINTENANCE_REPORTS,
  ACCESS_PROPERTIES,
  ACCESS_PROPERTY_MANAGEMENT,
  ACCESS_RENTALS,
  ACCESS_RENTALS_MENU,
  ACCESS_STATS_DASHBOARD,
  ACCESS_STATS_OCCUPANCY,
  ACCESS_STATS_PRICE_M2,
  ACCESS_STATS_REVENUE,
  ACCESS_STATS_UNITS,
  ACCESS_STATISTICS_MENU,
  ACCESS_PROSPECTS,
  ACCESS_TENANTS,
  ACCESS_RENT_PAYMENTS,
  ACCESS_USERS,
  usePermissions,
} from '../../hooks/usePermissions';
import { useAuth } from '../../contexts/AuthContext';
import { isInvestorRoleName, isTenantRoleName, isMaintenanceRoleName } from '../../utils/investorRole';

interface MenuItem {
  label: string;
  path: string;
  icon: React.ElementType;
  permission?: string | string[];
  children?: MenuItem[];
}

const allMenuItems: MenuItem[] = [
  { label: 'Dashboard', path: '/', icon: LayoutDashboard, permission: 'view_dashboard' },
  { label: 'Propiedades', path: '/propiedades', icon: Building2, permission: [...ACCESS_PROPERTIES] },
  {
    label: 'Rentas',
    path: '/rentas',
    icon: FileText,
    permission: [...ACCESS_RENTALS_MENU],
    children: [
      { label: 'Solicitudes', path: '/rentas/solicitudes', icon: ClipboardList, permission: [...ACCESS_APPLICATIONS] },
      { label: 'Prospectos', path: '/rentas/prospectos', icon: UserCheck, permission: [...ACCESS_PROSPECTS] },
      { label: 'Inquilinos', path: '/rentas/inquilinos', icon: Users, permission: [...ACCESS_TENANTS] },
      { label: 'Pagos', path: '/rentas/pagos', icon: Wallet, permission: [...ACCESS_RENT_PAYMENTS] },
    ],
  },
  { label: 'Inversionistas', path: '/inversionistas', icon: Users, permission: [...ACCESS_INVESTORS] },
  {
    label: 'Estadísticas',
    path: '/estadisticas',
    icon: BarChart3,
    permission: [...ACCESS_STATISTICS_MENU],
    children: [
      { label: 'Dashboard', path: '/estadisticas/dashboard', icon: LayoutDashboard, permission: [...ACCESS_STATS_DASHBOARD] },
      { label: 'Ocupación', path: '/estadisticas/ocupacion', icon: TrendingUp, permission: [...ACCESS_STATS_OCCUPANCY] },
      { label: 'Unidades', path: '/estadisticas/unidades', icon: Building2, permission: [...ACCESS_STATS_UNITS] },
      { label: 'Ingresos Brutos', path: '/estadisticas/ingresos-brutos', icon: DollarSign, permission: [...ACCESS_STATS_REVENUE] },
      { label: 'Valor m²', path: '/estadisticas/valor-m2', icon: Ruler, permission: [...ACCESS_STATS_PRICE_M2] },
    ],
  },
  { label: 'Administración de inmuebles', path: '/property-management', icon: Wrench, permission: [...ACCESS_PROPERTY_MANAGEMENT] },
  { label: 'Usuarios', path: '/usuarios', icon: Settings, permission: [...ACCESS_USERS] },
  {
    label: 'Configuración del sistema',
    path: '/configuracion-sistema',
    icon: SlidersHorizontal,
    permission: 'manage_settings',
  },
  // Configuración (contraseña y 2FA) — visible para cualquier usuario autenticado, sin permiso especial
  { label: 'Configuración', path: '/configuracion', icon: KeyRound },
];

interface SidebarProps {
  isMobileOpen: boolean;
  onMobileClose: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ isMobileOpen, onMobileClose }) => {
  const location = useLocation();
  const { user } = useAuth();
  const { hasAnyPermission, hasPermission } = usePermissions();
  const isInversor = isInvestorRoleName(user?.role?.name);
  const isTenant = isTenantRoleName(user?.role?.name);
  const isMaintenanceRole = isMaintenanceRoleName(user?.role?.name);
  const isMiCuentaSection =
    location.pathname === '/mi-cuenta' || location.pathname.startsWith('/mi-cuenta/');
  const showInvestorAccountShortcut =
    !isInversor &&
    !isTenant &&
    !isMaintenanceRole &&
    (hasPermission('investor_account') || hasPermission('view_investor_account'));
  // Solo mostrar la sección "Cuenta como inquilino" cuando el usuario ES rol inquilino.
  // Evita duplicar accesos (ej. admin con permisos de portal) y mantiene el menú consistente.
  const showTenantPortalShortcut = false;
  const roleName = user?.role?.name?.toLowerCase() ?? '';
  const isAdminOrDireccion = roleName === 'admin' || roleName === 'dirección' || roleName === 'direccion';
  const [collapsed, setCollapsed] = useLocalStorage('sidebar-collapsed', false);
  const [expandedGroups, setExpandedGroups] = useState<string[]>(() => {
    const path = location.pathname;
    if (path.startsWith('/rentas')) return ['rentas'];
    if (path.startsWith('/estadisticas')) return ['estadisticas'];
    if (path.startsWith('/property-management')) return [];
    return [];
  });

  // Inversor/Inquilino/Mantenimiento (rol): menú principal distinto o vacío
  const menuItems = useMemo(() => {
    if (isInversor || isTenant || isMaintenanceRole) return [];
    const hasAccess = (permission?: MenuItem['permission']) => {
      if (!permission) return true;
      return Array.isArray(permission) ? hasAnyPermission(permission) : hasAnyPermission([permission]);
    };

    const filterMenu = (items: MenuItem[]): MenuItem[] => {
      return items
        .map((item) => {
          // Nunca mutar allMenuItems (evita que queden children "pegados" entre renders/usuarios)
          const next: MenuItem = { ...item };
          if (next.children && next.children.length > 0) {
            next.children = filterMenu(next.children);
          }
          return next;
        })
        .filter((item) => {
          // Si no tiene permiso definido, mostrar salvo Configuración cuando solo tiene Fideicomiso (la verá en sección Fideicomiso)
          if (!item.permission) {
            if (
              item.path === '/configuracion' &&
              hasAnyPermission(['view_fideicomiso']) &&
              !hasAnyPermission(['view_dashboard'])
            )
              return false;
            return true;
          }

          // Permiso del item principal
          if (!hasAccess(item.permission)) return false;

          // Si tiene hijos, mostrar solo si queda al menos uno permitido
          if (item.children) {
            return item.children.length > 0;
          }

          return true;
        });
    };

    return filterMenu(allMenuItems);
  }, [hasAnyPermission, isInversor, isTenant, isMaintenanceRole]);

  const toggleGroup = (path: string) => {
    setExpandedGroups((prev) =>
      prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path]
    );
  };

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const isChildActive = (children: MenuItem[]) => {
    return children.some((child) => location.pathname === child.path);
  };

  const renderMenuItem = (item: MenuItem, level = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedGroups.includes(item.path);
    const active = isActive(item.path);
    const childActive = hasChildren && isChildActive(item.children!);

    if (hasChildren) {
      return (
        <div key={item.path}>
          <button
            onClick={() => toggleGroup(item.path)}
            className={`w-full flex items-center justify-between px-4 py-3 text-white/70 hover:text-white/90 transition-colors rounded-lg text-sm ${
              childActive 
                ? 'text-white' 
                : 'hover:bg-[#33477B]'
            }`}
            style={childActive ? { backgroundColor: '#1565D8' } : {}}
          >
            <div className="flex items-center space-x-3">
              <item.icon className="w-5 h-5" />
              {!collapsed && <span>{item.label}</span>}
            </div>
            {!collapsed && (isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />)}
          </button>
          {!collapsed && (
            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <div className="pl-4 pt-1">
                {item.children!.map((child) => {
                  const childActive = location.pathname === child.path;
                  return (
                  <Link
                    key={child.path}
                    to={child.path}
                    onClick={onMobileClose}
                    className={`flex items-center space-x-3 px-4 py-2 text-white/70 hover:text-white/90 transition-colors rounded-lg w-full text-sm ${
                      childActive 
                        ? 'text-white' 
                        : 'hover:bg-[#33477B]'
                    }`}
                    style={childActive ? { backgroundColor: '#1565D8' } : {}}
                  >
                    <child.icon className="w-4 h-4" />
                    <span>{child.label}</span>
                  </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      );
    }

    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={onMobileClose}
        className={`flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-white/90 transition-colors rounded-lg w-full text-sm ${
          active 
            ? 'text-white' 
            : 'hover:bg-[#33477B]'
        }`}
        style={active ? { backgroundColor: '#1565D8' } : {}}
      >
        <item.icon className="w-5 h-5" />
        {!collapsed && <span>{item.label}</span>}
      </Link>
    );
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onMobileClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 bg-primary transition-all duration-200 ${
          collapsed ? 'w-16' : 'w-64'
        } ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-4 border-b border-white/10">
            {!collapsed && (
              <h1 className="text-white text-xl font-bold">JNU Group</h1>
            )}
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="lg:flex hidden text-white/70 hover:text-white/90 p-2"
            >
              <Menu className="w-5 h-5" />
            </button>
            <button
              onClick={onMobileClose}
              className="lg:hidden text-white/70 hover:text-white/90 p-2"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Menu Items */}
          <nav className="flex-1 overflow-y-auto py-4 px-2 flex flex-col gap-[5px]">
            {isMaintenanceRole ? (
              <>
                {!collapsed ? (
                  <div className="px-4 py-2 space-y-1">
                    <h3 className="text-[10px] font-semibold text-white/60 uppercase tracking-wider mb-2">
                      Mantenimiento
                    </h3>
                    <Link
                      to="/maintenance"
                      onClick={onMobileClose}
                      className={`flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-white/90 transition-colors rounded-lg w-full text-sm ${
                        location.pathname === '/maintenance' ? 'text-white' : 'hover:bg-[#33477B]'
                      }`}
                      style={location.pathname === '/maintenance' ? { backgroundColor: '#1565D8' } : {}}
                    >
                      <ClipboardList className="w-5 h-5" />
                      <span>Reportes de mantenimiento</span>
                    </Link>
                    <Link
                      to="/maintenance/account"
                      onClick={onMobileClose}
                      className={`flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-white/90 transition-colors rounded-lg w-full text-sm ${
                        location.pathname === '/maintenance/account' ? 'text-white' : 'hover:bg-[#33477B]'
                      }`}
                      style={location.pathname === '/maintenance/account' ? { backgroundColor: '#1565D8' } : {}}
                    >
                      <UserCircle className="w-5 h-5" />
                      <span>Mi cuenta</span>
                    </Link>
                    <Link
                      to="/delivery-inspections"
                      onClick={onMobileClose}
                      className={`flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-white/90 transition-colors rounded-lg w-full text-sm ${
                        location.pathname === '/delivery-inspections' ? 'text-white' : 'hover:bg-[#33477B]'
                      }`}
                      style={location.pathname === '/delivery-inspections' ? { backgroundColor: '#1565D8' } : {}}
                    >
                      <ClipboardCheck className="w-5 h-5" />
                      <span>Inspecciones de entrega</span>
                    </Link>
                    <Link
                      to="/configuracion"
                      onClick={onMobileClose}
                      className={`flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-white/90 transition-colors rounded-lg w-full text-sm ${
                        location.pathname === '/configuracion' ? 'text-white' : 'hover:bg-[#33477B]'
                      }`}
                      style={location.pathname === '/configuracion' ? { backgroundColor: '#1565D8' } : {}}
                    >
                      <KeyRound className="w-5 h-5" />
                      <span>Configuración</span>
                    </Link>
                  </div>
                ) : (
                  <>
                    <Link
                      to="/maintenance"
                      onClick={onMobileClose}
                      className={`flex items-center justify-center px-4 py-3 text-white/70 hover:text-white/90 transition-colors rounded-lg w-full text-sm ${
                        location.pathname === '/maintenance' ? 'text-white' : 'hover:bg-[#33477B]'
                      }`}
                      style={location.pathname === '/maintenance' ? { backgroundColor: '#1565D8' } : {}}
                      title="Reportes de mantenimiento"
                    >
                      <ClipboardList className="w-5 h-5" />
                    </Link>
                    <Link
                      to="/maintenance/account"
                      onClick={onMobileClose}
                      className={`flex items-center justify-center px-4 py-3 text-white/70 hover:text-white/90 transition-colors rounded-lg w-full text-sm ${
                        location.pathname === '/maintenance/account' ? 'text-white' : 'hover:bg-[#33477B]'
                      }`}
                      style={location.pathname === '/maintenance/account' ? { backgroundColor: '#1565D8' } : {}}
                      title="Mi cuenta"
                    >
                      <UserCircle className="w-5 h-5" />
                    </Link>
                    <Link
                      to="/delivery-inspections"
                      onClick={onMobileClose}
                      className={`flex items-center justify-center px-4 py-3 text-white/70 hover:text-white/90 transition-colors rounded-lg w-full text-sm ${
                        location.pathname === '/delivery-inspections' ? 'text-white' : 'hover:bg-[#33477B]'
                      }`}
                      style={location.pathname === '/delivery-inspections' ? { backgroundColor: '#1565D8' } : {}}
                      title="Inspecciones de entrega"
                    >
                      <ClipboardCheck className="w-5 h-5" />
                    </Link>
                    <Link
                      to="/configuracion"
                      onClick={onMobileClose}
                      className={`flex items-center justify-center px-4 py-3 text-white/70 hover:text-white/90 transition-colors rounded-lg w-full text-sm ${
                        location.pathname === '/configuracion' ? 'text-white' : 'hover:bg-[#33477B]'
                      }`}
                      style={location.pathname === '/configuracion' ? { backgroundColor: '#1565D8' } : {}}
                      title="Configuración"
                    >
                      <KeyRound className="w-5 h-5" />
                    </Link>
                  </>
                )}
              </>
            ) : (
              <>
                {menuItems.map((item) => renderMenuItem(item))}

                {/* Portales inversionista / inquilino (solo con rol o permiso correspondiente) */}
                {!collapsed &&
                  (isInversor || isTenant || showInvestorAccountShortcut) && (
                    <>
                      <div className="border-t border-white/10 my-2"></div>
                      <div className="px-4 py-2">
                        {isInversor ? (
                          <>
                            <Link
                              to="/mi-cuenta"
                              onClick={onMobileClose}
                              className={`flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-white/90 transition-colors rounded-lg w-full text-sm ${
                                isMiCuentaSection ? 'text-white' : 'hover:bg-[#33477B]'
                              }`}
                              style={isMiCuentaSection ? { backgroundColor: '#1565D8' } : {}}
                            >
                              <UserCircle className="w-5 h-5" />
                              <span>Mi cuenta</span>
                            </Link>
                            {!isAdminOrDireccion && (
                              <Link
                                to="/configuracion"
                                onClick={onMobileClose}
                                className={`flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-white/90 transition-colors rounded-lg w-full text-sm ${
                                  location.pathname === '/configuracion' ? 'text-white' : 'hover:bg-[#33477B]'
                                }`}
                                style={location.pathname === '/configuracion' ? { backgroundColor: '#1565D8' } : {}}
                              >
                                <Settings className="w-5 h-5" />
                                <span>Configuración</span>
                              </Link>
                            )}
                          </>
                        ) : isTenant ? (
                          <>
                            <h3 className="text-[10px] font-semibold text-white/60 uppercase tracking-wider mb-2">
                              Cuenta como inquilino
                            </h3>
                            <Link
                              to="/inquilino"
                              onClick={onMobileClose}
                              className={`flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-white/90 transition-colors rounded-lg w-full text-sm ${
                                location.pathname === '/inquilino' ? 'text-white' : 'hover:bg-[#33477B]'
                              }`}
                              style={location.pathname === '/inquilino' ? { backgroundColor: '#1565D8' } : {}}
                            >
                              <UserCircle className="w-5 h-5" />
                              <span>Mi cuenta</span>
                            </Link>
                            <Link
                              to="/configuracion"
                              onClick={onMobileClose}
                              className={`flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-white/90 transition-colors rounded-lg w-full text-sm ${
                                location.pathname === '/configuracion' ? 'text-white' : 'hover:bg-[#33477B]'
                              }`}
                              style={location.pathname === '/configuracion' ? { backgroundColor: '#1565D8' } : {}}
                            >
                              <Settings className="w-5 h-5" />
                              <span>Configuración</span>
                            </Link>
                          </>
                        ) : showInvestorAccountShortcut ? (
                          <>
                            <h3 className="text-[10px] font-semibold text-white/60 uppercase tracking-wider mb-2">
                              Cuenta como inversionista
                            </h3>
                            <Link
                              to="/mi-cuenta"
                              onClick={onMobileClose}
                              className={`flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-white/90 transition-colors rounded-lg w-full text-sm ${
                                isMiCuentaSection ? 'text-white' : 'hover:bg-[#33477B]'
                              }`}
                              style={isMiCuentaSection ? { backgroundColor: '#1565D8' } : {}}
                            >
                              <UserCircle className="w-5 h-5" />
                              <span>Mi cuenta</span>
                            </Link>
                          </>
                        ) : null}
                      </div>
                    </>
                  )}
                {collapsed &&
                  (isInversor || isTenant || showInvestorAccountShortcut) && (
                    <>
                      <div className="border-t border-white/10 my-2"></div>
                      <Link
                        to={
                          isTenant
                            ? '/inquilino'
                            : '/mi-cuenta'
                        }
                        onClick={onMobileClose}
                        className={`flex items-center justify-center px-4 py-3 text-white/70 hover:text-white/90 transition-colors rounded-lg w-full text-sm ${
                          (isTenant
                            ? location.pathname === '/inquilino'
                            : isMiCuentaSection)
                            ? 'text-white'
                            : 'hover:bg-[#33477B]'
                        }`}
                        style={
                          (isTenant
                            ? location.pathname === '/inquilino'
                            : isMiCuentaSection)
                            ? { backgroundColor: '#1565D8' }
                            : {}
                        }
                        title="Mi cuenta"
                      >
                        <UserCircle className="w-5 h-5" />
                      </Link>
                      {(isInversor || isTenant) && !isAdminOrDireccion ? (
                        <Link
                          to="/configuracion"
                          onClick={onMobileClose}
                          className={`flex items-center justify-center px-4 py-3 text-white/70 hover:text-white/90 transition-colors rounded-lg w-full text-sm ${
                            location.pathname === '/configuracion' ? 'text-white' : 'hover:bg-[#33477B]'
                          }`}
                          style={location.pathname === '/configuracion' ? { backgroundColor: '#1565D8' } : {}}
                          title="Configuración"
                        >
                          <Settings className="w-5 h-5" />
                        </Link>
                      ) : null}
                    </>
                  )}

                {/* Fideicomiso / KYC (oculto para Inversor y rol Mantenimiento; visible solo con permiso view_fideicomiso) */}
                {!isInversor &&
                  !isMaintenanceRole &&
                  hasAnyPermission(['view_fideicomiso']) &&
                  !collapsed && (
              <>
                <div className="border-t border-white/10 my-2"></div>
                <div className="px-4 py-2">
                  <h3 className="text-[10px] font-semibold text-white/60 uppercase tracking-wider mb-2">
                    Fideicomiso
                  </h3>
                  <Link
                    to="/kyc"
                    onClick={onMobileClose}
                    className={`flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-white/90 transition-colors rounded-lg w-full text-sm ${
                      location.pathname === '/kyc' ? 'text-white' : 'hover:bg-[#33477B]'
                    }`}
                    style={location.pathname === '/kyc' ? { backgroundColor: '#1565D8' } : {}}
                  >
                    <ShieldCheck className="w-5 h-5" />
                    <span>KYC</span>
                  </Link>
                  {/* Configuración solo para quien NO tiene menú principal (ej. solo rol Fideicomiso) para que pueda cambiar contraseña y 2FA */}
                  {!isAdminOrDireccion && !hasAnyPermission(['view_dashboard']) && (
                    <Link
                      to="/configuracion"
                      onClick={onMobileClose}
                      className={`flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-white/90 transition-colors rounded-lg w-full text-sm ${
                        location.pathname === '/configuracion' ? 'text-white' : 'hover:bg-[#33477B]'
                      }`}
                      style={location.pathname === '/configuracion' ? { backgroundColor: '#1565D8' } : {}}
                    >
                      <Settings className="w-5 h-5" />
                      <span>Configuración</span>
                    </Link>
                  )}
                </div>
              </>
            )}
            {!isInversor && !isMaintenanceRole && hasAnyPermission(['view_fideicomiso']) && collapsed && (
              <>
                <div className="border-t border-white/10 my-2"></div>
                <Link
                  to="/kyc"
                  onClick={onMobileClose}
                  className={`flex items-center justify-center px-4 py-3 text-white/70 hover:text-white/90 transition-colors rounded-lg w-full text-sm ${
                    location.pathname === '/kyc' ? 'text-white' : 'hover:bg-[#33477B]'
                  }`}
                  style={location.pathname === '/kyc' ? { backgroundColor: '#1565D8' } : {}}
                  title="KYC"
                >
                  <ShieldCheck className="w-5 h-5" />
                </Link>
                {!isAdminOrDireccion && !hasAnyPermission(['view_dashboard']) && (
                  <Link
                    to="/configuracion"
                    onClick={onMobileClose}
                    className={`flex items-center justify-center px-4 py-3 text-white/70 hover:text-white/90 transition-colors rounded-lg w-full text-sm ${
                      location.pathname === '/configuracion' ? 'text-white' : 'hover:bg-[#33477B]'
                    }`}
                    style={location.pathname === '/configuracion' ? { backgroundColor: '#1565D8' } : {}}
                    title="Configuración"
                  >
                    <Settings className="w-5 h-5" />
                  </Link>
                )}
              </>
            )}

            {/* Mantenimiento de inmuebles (staff con permiso; no duplicar con rol Mantenimiento) */}
            {!isInversor &&
              !isMaintenanceRole &&
              hasAnyPermission([...ACCESS_MAINTENANCE_REPORTS]) &&
              !collapsed && (
              <>
                <div className="border-t border-white/10 my-2"></div>
                <div className="px-4 py-2">
                  <h3 className="text-[10px] font-semibold text-white/60 uppercase tracking-wider mb-2">
                    Mantenimiento
                  </h3>
                  <Link
                    to="/maintenance"
                    onClick={onMobileClose}
                    className={`flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-white/90 transition-colors rounded-lg w-full text-sm ${
                      location.pathname === '/maintenance' ? 'text-white' : 'hover:bg-[#33477B]'
                    }`}
                    style={location.pathname === '/maintenance' ? { backgroundColor: '#1565D8' } : {}}
                  >
                    <ClipboardList className="w-5 h-5" />
                    <span>Reportes de mantenimiento</span>
                  </Link>
                  <Link
                    to="/maintenance/account"
                    onClick={onMobileClose}
                    className={`flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-white/90 transition-colors rounded-lg w-full text-sm ${
                      location.pathname === '/maintenance/account' ? 'text-white' : 'hover:bg-[#33477B]'
                    }`}
                    style={location.pathname === '/maintenance/account' ? { backgroundColor: '#1565D8' } : {}}
                  >
                    <UserCircle className="w-5 h-5" />
                    <span>Mi cuenta</span>
                  </Link>
                  {!isAdminOrDireccion && (
                    <Link
                      to="/maintenance/settings"
                      onClick={onMobileClose}
                      className={`flex items-center space-x-3 px-4 py-3 text-white/70 hover:text-white/90 transition-colors rounded-lg w-full text-sm ${
                        location.pathname === '/maintenance/settings' ? 'text-white' : 'hover:bg-[#33477B]'
                      }`}
                      style={location.pathname === '/maintenance/settings' ? { backgroundColor: '#1565D8' } : {}}
                    >
                      <Settings className="w-5 h-5" />
                      <span>Configuración</span>
                    </Link>
                  )}
                </div>
              </>
            )}
            {!isInversor &&
              !isMaintenanceRole &&
              hasAnyPermission([...ACCESS_MAINTENANCE_REPORTS]) &&
              collapsed && (
                <>
                  <div className="border-t border-white/10 my-2"></div>
                  <Link
                    to="/maintenance"
                    onClick={onMobileClose}
                    className={`flex items-center justify-center px-4 py-3 text-white/70 hover:text-white/90 transition-colors rounded-lg w-full text-sm ${
                      location.pathname === '/maintenance' ? 'text-white' : 'hover:bg-[#33477B]'
                    }`}
                    style={location.pathname === '/maintenance' ? { backgroundColor: '#1565D8' } : {}}
                    title="Reportes de mantenimiento"
                  >
                    <ClipboardList className="w-5 h-5" />
                  </Link>
                  <Link
                    to="/maintenance/account"
                    onClick={onMobileClose}
                    className={`flex items-center justify-center px-4 py-3 text-white/70 hover:text-white/90 transition-colors rounded-lg w-full text-sm ${
                      location.pathname === '/maintenance/account' ? 'text-white' : 'hover:bg-[#33477B]'
                    }`}
                    style={location.pathname === '/maintenance/account' ? { backgroundColor: '#1565D8' } : {}}
                    title="Mi cuenta"
                  >
                    <UserCircle className="w-5 h-5" />
                  </Link>
                  {!isAdminOrDireccion && (
                    <Link
                      to="/maintenance/settings"
                      onClick={onMobileClose}
                      className={`flex items-center justify-center px-4 py-3 text-white/70 hover:text-white/90 transition-colors rounded-lg w-full text-sm ${
                        location.pathname === '/maintenance/settings' ? 'text-white' : 'hover:bg-[#33477B]'
                      }`}
                      style={location.pathname === '/maintenance/settings' ? { backgroundColor: '#1565D8' } : {}}
                      title="Configuración"
                    >
                      <Settings className="w-5 h-5" />
                    </Link>
                  )}
                </>
              )}
              </>
            )}
          </nav>
        </div>
      </aside>
    </>
  );
};

